/**
 * Every sound in the game, described as data.
 *
 * Pure: no Web Audio, no Phaser. AudioManager renders these; keeping the
 * recipes separate means the sound design can be reasoned about and tested
 * without a browser, and nothing here can accidentally make a noise.
 *
 * All of it is synthesised. There is no sampled audio in the project, so there
 * is nothing to licence and nothing to download (CLAUDE.md).
 *
 * The brief is a game for 8-year-olds, which sets the rules: everything is
 * short, quiet and soft-edged. Nothing uses a square or sawtooth wave, the
 * mistake sound is lower and gentler than the success sound rather than
 * harsher, and no single sound peaks above 0.2 before the channel volume.
 */

import type { BackgroundTheme } from '../types';

export type Wave = 'sine' | 'triangle';

/** One voice of a sound: a pitched glide, or a puff of noise. */
export interface Tone {
  readonly wave?: Wave;
  /** Starting frequency in Hz. Ignored when `noise` is set. */
  readonly from: number;
  /** Glides here over the tone's life; defaults to `from` (a steady pitch). */
  readonly to?: number;
  /** Seconds. */
  readonly duration: number;
  /** Peak gain, before the channel volume. */
  readonly gain: number;
  /** Seconds spent rising to peak; the remainder decays. */
  readonly attack?: number;
  /** Seconds to wait before this voice starts, for chords and arpeggios. */
  readonly delay?: number;
  /** Filtered noise instead of a pitched oscillator; `from` is the cutoff. */
  readonly noise?: boolean;
}

export type SfxName =
  | 'key'
  | 'correct'
  | 'wrong'
  | 'jump'
  | 'land'
  | 'spring'
  | 'jetpack'
  | 'star'
  | 'unlock'
  | 'heartbeat'
  | 'splash';

export const SFX: Readonly<Record<SfxName, readonly Tone[]>> = {
  /**
   * Typing fires this on every keystroke, so it has to be nearly subliminal -
   * and, more than any other sound, immediate.
   *
   * Built as a tick, not a note. A real key click is a broadband transient;
   * a pure tone needs a couple of cycles before the ear registers it, so a
   * tonal blip reads as softer and later than the key that caused it. A short
   * burst of filtered noise is heard on its first sample, with a faint bright
   * tap on top so it is not just hiss. No attack and no glide on either.
   */
  key: [
    { noise: true, from: 5200, duration: 0.018, gain: 0.09, attack: 0 },
    { wave: 'triangle', from: 2300, duration: 0.014, gain: 0.025, attack: 0 },
  ],

  correct: [
    { wave: 'sine', from: 880, duration: 0.09, gain: 0.13, attack: 0.004 },
    { wave: 'sine', from: 1320, duration: 0.14, gain: 0.1, attack: 0.004, delay: 0.05 },
  ],

  /**
   * Lower and softer than `correct`, never sharper.
   *
   * CLAUDE.md: a mistake is answered gently, so this drops in pitch and fades
   * rather than buzzing. It is the one sound a struggling child hears most.
   */
  wrong: [{ wave: 'sine', from: 330, to: 246, duration: 0.16, gain: 0.11, attack: 0.012 }],

  jump: [{ wave: 'triangle', from: 420, to: 760, duration: 0.16, gain: 0.12, attack: 0.006 }],

  land: [
    { wave: 'sine', from: 240, to: 150, duration: 0.12, gain: 0.14, attack: 0.004 },
    { noise: true, from: 1800, duration: 0.06, gain: 0.05 },
  ],

  spring: [
    { wave: 'sine', from: 300, to: 900, duration: 0.1, gain: 0.15, attack: 0.004 },
    { wave: 'sine', from: 900, to: 520, duration: 0.16, gain: 0.12, delay: 0.09 },
  ],

  jetpack: [
    { noise: true, from: 900, duration: 0.42, gain: 0.1, attack: 0.09 },
    { wave: 'sine', from: 160, to: 300, duration: 0.42, gain: 0.05, attack: 0.09 },
  ],

  star: [
    { wave: 'sine', from: 1047, duration: 0.12, gain: 0.1, attack: 0.003 },
    { wave: 'sine', from: 1319, duration: 0.12, gain: 0.1, attack: 0.003, delay: 0.07 },
    { wave: 'sine', from: 1568, duration: 0.2, gain: 0.1, attack: 0.003, delay: 0.14 },
  ],

  unlock: [
    { wave: 'triangle', from: 523, duration: 0.14, gain: 0.13, attack: 0.005 },
    { wave: 'triangle', from: 659, duration: 0.14, gain: 0.13, attack: 0.005, delay: 0.1 },
    { wave: 'triangle', from: 784, duration: 0.14, gain: 0.13, attack: 0.005, delay: 0.2 },
    { wave: 'triangle', from: 1047, duration: 0.34, gain: 0.14, attack: 0.008, delay: 0.3 },
  ],

  /**
   * Tide Run, with the water close: a soft lub-dub, low enough to feel more
   * than hear. It repeats while the water is near, so it must stay gentle.
   */
  heartbeat: [
    { wave: 'sine', from: 70, to: 55, duration: 0.12, gain: 0.16, attack: 0.01 },
    { wave: 'sine', from: 62, to: 50, duration: 0.14, gain: 0.12, attack: 0.01, delay: 0.18 },
  ],

  /** Caught by the tide: a soft splash, never a crash. */
  splash: [
    { noise: true, from: 2400, duration: 0.5, gain: 0.12, attack: 0.02 },
    { wave: 'sine', from: 420, to: 140, duration: 0.35, gain: 0.08, attack: 0.01 },
  ],
};

/** How long a sound lasts, counting the latest voice's delay. */
export function sfxDuration(name: SfxName): number {
  return SFX[name].reduce((longest, tone) => {
    return Math.max(longest, (tone.delay ?? 0) + tone.duration);
  }, 0);
}

/* ------------------------------------------------------------------ music */

/** Equal temperament, A4 = 440Hz. */
export function noteHz(semitonesFromA4: number): number {
  return 440 * Math.pow(2, semitonesFromA4 / 12);
}

/**
 * Major pentatonic.
 *
 * Every pair of notes in it sounds consonant, so a melody picked at random
 * from it cannot land on a sour interval. That is what lets the music be
 * generated rather than composed, and still be calm for an hour of play.
 */
export const PENTATONIC: readonly number[] = [0, 2, 4, 7, 9];

export interface MusicBed {
  /** Root note, in semitones from A4. */
  readonly root: number;
  /** Seconds between melody notes. */
  readonly beat: number;
  readonly wave: Wave;
  /** Peak gain of one melody note, before the channel volume. */
  readonly gain: number;
  /** How far up the scale the melody may wander, in scale degrees. */
  readonly range: number;
}

/**
 * One bed per environment, so the music changes as the climb does.
 *
 * Low and slow for the dark bands, brighter and a little quicker for the open
 * ones. Nothing is faster than one note every 1.4s: this plays under a child
 * concentrating on spelling, so it has to stay out of the way.
 */
const BEDS: Readonly<Record<BackgroundTheme, MusicBed>> = {
  sunset: { root: -9, beat: 1.7, wave: 'sine', gain: 0.07, range: 8 },
  'midday-glacier': { root: -2, beat: 1.5, wave: 'sine', gain: 0.07, range: 9 },
  'snowstorm-dusk': { root: -14, beat: 2.0, wave: 'sine', gain: 0.06, range: 6 },
  'aurora-night': { root: -12, beat: 2.2, wave: 'sine', gain: 0.06, range: 7 },
  'iceberg-sea': { root: -7, beat: 1.8, wave: 'sine', gain: 0.07, range: 8 },
  'research-station': { root: -10, beat: 1.9, wave: 'triangle', gain: 0.06, range: 6 },
  'ice-cave': { root: -17, beat: 2.3, wave: 'sine', gain: 0.06, range: 6 },
  blizzard: { root: -5, beat: 1.6, wave: 'sine', gain: 0.06, range: 7 },
  'midnight-sun': { root: -4, beat: 1.5, wave: 'triangle', gain: 0.07, range: 9 },
  'grand-aurora': { root: -12, beat: 1.4, wave: 'triangle', gain: 0.07, range: 10 },
};

export function musicBedFor(theme: BackgroundTheme): MusicBed {
  return BEDS[theme];
}

/**
 * The next melody note, as a scale degree in [0, range].
 *
 * Small steps most of the time, so the line drifts rather than leaps, with a
 * gentle pull back toward the middle of the range. It never repeats a note:
 * a step that would leave the range bounces back off the edge instead. (It
 * used to drift upward and clamp, so it stuck on the top note and played the
 * same bright "ding" over and over.)
 */
export function nextDegree(degree: number, range: number, random: () => number): number {
  const top = Math.max(1, Math.round(range));
  const middle = top / 2;
  // Below the middle, a little more likely to go up; above it, down.
  const upChance = 0.5 + Math.max(-0.2, Math.min(0.2, ((middle - degree) / top) * 0.4));
  const size = random() < 0.75 ? 1 : 2;
  const step = random() < upChance ? size : -size;
  let next = degree + step;
  if (next > top) next = top - (next - top);
  if (next < 0) next = -next;
  next = Math.max(0, Math.min(top, next));
  return next === degree ? (degree >= top ? degree - 1 : degree + 1) : next;
}

/**
 * The note `degree` steps up the pentatonic scale from the bed's root.
 *
 * Wraps into higher octaves rather than running out, so `range` can exceed the
 * five notes the scale actually has.
 */
export function scaleNote(bed: MusicBed, degree: number): number {
  const steps = PENTATONIC.length;
  const octave = Math.floor(degree / steps);
  const within = ((degree % steps) + steps) % steps;
  return bed.root + octave * 12 + (PENTATONIC[within] ?? 0);
}
